// =============================================================================
//  L'ASSISTANT DE RELEVÉ — le visiteur RÉPOND, il ne dessine pas (DEC-034)
// =============================================================================
//  🔴 CE QU'IL RÉPARE. Le backend du relevé existait depuis le lot 26/26 :
//  `GET /dal/dossiers/{id}/releve/{zone}`, `POST …/reponse`, et le plan se
//  reconstruit à chaque réponse. ⛔ Aucun écran ne l'appelait. C'était une
//  capacité en dette — la même situation que l'annuaire.
//
//  🔑 UNE QUESTION À LA FOIS. Sur un palier, chez un bénéficiaire âgé, un
//  formulaire de onze champs ne se remplit pas : il se survole. L'assistant
//  pose ce qu'il faut, dans l'ordre où la pièce se mesure — on borne la pièce
//  AVANT d'y poser une porte.
//
//  ⚠️ LE PLAN SE PRÉCONSTRUIT PENDANT LES RÉPONSES. Ce n'est pas décoratif :
//  c'est le seul retour qui dit à l'opérateur que sa cote a été comprise. Une
//  pièce qui se dessine de travers se corrige sur place ; une erreur découverte
//  au bureau impose une seconde visite.
//
//  ⛔ IL NE DESSINE RIEN. Le tracé est DÉRIVÉ des réponses (DEC-034). Aucun
//  glisser-déposer, aucune poignée : ce serait l'éditeur bureau, et il vient
//  après.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, ChevronLeft, Loader2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const ETAPES = {
  forme: "La pièce",
  ouvertures: "La porte et les ouvertures",
  equipements: "Les équipements",
  constat: "Ce que vous constatez",
};

const cadre = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 14,
};
const saisie = {
  width: "100%", height: 52, borderRadius: 10, padding: "0 14px",
  fontSize: 20, background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", fontVariantNumeric: "tabular-nums",
};
const primaire = {
  width: "100%", minHeight: 52, borderRadius: 10, border: "none", fontSize: 16,
  cursor: "pointer", background: "var(--oh-accent-solid)",
  color: "var(--oh-accent-contrast)", display: "flex", alignItems: "center",
  justifyContent: "center", gap: 8,
};
const discret = {
  minHeight: 44, borderRadius: 10, padding: "0 14px", fontSize: 14,
  cursor: "pointer", background: "transparent",
  border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)",
};

/** Le plan, DÉRIVÉ de la géométrie rendue par le serveur.
 *
 * ⚠️ On dessine ce que le SERVEUR a calculé — ⛔ pas une projection locale des
 * saisies. Deux calculs de la même forme divergeraient, et l'opérateur
 * validerait un dessin que le DAL ne reproduirait pas.
 */
function PlanEnConstruction({ geometrie, surface }) {
  // 🔴 LE MÊME ÉCRAN SERT EN VISITE ET AU BUREAU (DEC-034). Un SVG en
  // `width: 100%` avec une viewBox fixe met TOUT à l'échelle — traits ET
  // textes. Sur un 27 pouces, « 340 cm » devenait énorme et les murs épais
  // comme des poutres ; sur téléphone, c'était juste.
  // ⇒ On MESURE la largeur réellement disponible, et on exprime les tailles
  // en unités de viewBox de façon que le RENDU fasse toujours le même nombre
  // de pixels CSS. ⛔ Pas de point de rupture arbitraire : la formule vaut
  // pour toutes les largeurs, y compris celles qu'on n'a pas testées.
  const boiteRef = useRef(null);
  const [largeurRendue, setLargeurRendue] = useState(0);
  useEffect(() => {
    const el = boiteRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const obs = new ResizeObserver(([e]) => setLargeurRendue(e.contentRect.width));
    obs.observe(el);
    setLargeurRendue(el.getBoundingClientRect().width);
    return () => obs.disconnect();
  }, []);

  const sommets = (geometrie && geometrie.sommets) || [];
  if (sommets.length < 3) {
    return (
      <div style={{ ...cadre, textAlign: "center", padding: 20 }}
           data-testid="releve-plan-vide">
        <p style={{ fontSize: 13, color: "var(--oh-text-secondary)",
                    lineHeight: 1.6, margin: 0 }}>
          {/* ⚠️ On dit ce qui MANQUE, ⛔ pas « pas de plan » : l'opérateur doit
              savoir quel geste fait apparaître le dessin. */}
          Le plan se dessinera dès que la longueur et la largeur seront saisies.
        </p>
      </div>
    );
  }

  // ⚠️ LE CADRE TIENT COMPTE DES COTATIONS. Elles sont posées À L'EXTÉRIEUR
  // des murs : ne borner que sur les sommets les ferait sortir du dessin —
  // exactement le défaut corrigé sur le PDF au lot 1.
  const cotations = geometrie.cotations || [];
  const ouvertures = geometrie.ouvertures || [];
  const tousX = [...sommets.map((s) => s.x_cm), ...cotations.map((c) => c.x_cm)];
  const tousY = [...sommets.map((s) => s.y_cm), ...cotations.map((c) => c.y_cm)];
  const minX = Math.min(...tousX), maxX = Math.max(...tousX);
  const minY = Math.min(...tousY), maxY = Math.max(...tousY);
  const largeur = Math.max(maxX - minX, 1), hauteur = Math.max(maxY - minY, 1);
  // ⚠️ 420 de côté au lieu de 280 : le SVG est mis à l'échelle par le
  // navigateur, mais une boîte plus grande donne plus de place RELATIVE aux
  // textes de cote. ⛔ Sur un palier, un plan qu'on devine ne sert à rien.
  // 🔴 UNE UNITÉ DE VIEWBOX = UN PIXEL À L'ÉCRAN. C'est la seule façon d'en
  // finir avec la mise à l'échelle inverse : les tailles écrites (14, 48…)
  // SONT des pixels, quelle que soit la largeur du navigateur.
  // ⚠️ Ma version précédente calculait un facteur `u(px)` — et la MARGE, elle,
  // restait fixe : sur mobile « 200 cm » débordait de la viewBox et sortait
  // COUPÉ à gauche. ⛔ Exactement le défaut corrigé une heure plus tôt sur le
  // PDF (`MARGE_PT`), que je n'avais pas reporté ici : une marge doit suivre
  // la typo qu'elle abrite.
  const MARGE = 52;              // ⚠️ de quoi loger « 340 cm » à 14 px
  const LARGEUR_DEFAUT = 340;    // ⛔ jamais rien : repli avant la 1re mesure
  const dispo = Math.max((largeurRendue || LARGEUR_DEFAUT) - 2 * MARGE, 80);
  const echelle = dispo / largeur;
  const boiteL = largeur * echelle + 2 * MARGE;
  const boiteH = hauteur * echelle + 2 * MARGE;
  const X = (v) => MARGE + (v - minX) * echelle;
  const Y = (v) => MARGE + (v - minY) * echelle;
  const contour = sommets.map((s) => `${X(s.x_cm)},${Y(s.y_cm)}`).join(" ");

  const cx = sommets.reduce((a, s) => a + s.x_cm, 0) / sommets.length;
  const cy = sommets.reduce((a, s) => a + s.y_cm, 0) / sommets.length;

  const TEINTE = { porte: "var(--oh-accent-solid)",
                   fenetre: "var(--oh-stat-cyan-text)" };
  const SIGLE = { porte: "P", fenetre: "F", porte_fenetre: "PF", baie: "B" };

  return (
    <div style={{ ...cadre, padding: 8 }} data-testid="releve-plan">
      {/* 🔴 L'OBSERVATEUR EST SUR LE SVG, ⛔ PAS SUR LE CONTENEUR. Mesuré :
          sur un écran de 1440, le conteneur fait 1392 px mais le SVG est borné
          à 760 — mesurer le conteneur donnait un facteur 0,34 et des cotes
          rendues à 4,75 px, illisibles. On mesure CE QUI EST DESSINÉ. */}
      <svg ref={boiteRef} viewBox={`0 0 ${boiteL} ${boiteH}`} width="100%"
           style={{ width: "100%", display: "block",
                    // ⚠️ Borné au bureau : au-delà, le plan gagne en taille
                    // sans gagner en lisibilité, et pousse le questionnaire
                    // hors de l'écran. ⛔ Aucune borne sur mobile.
                    maxWidth: 760, margin: "0 auto" }} role="img"
           aria-label={`Plan de la pièce, ${surface || "?"} m², `
                       + `${ouvertures.length} ouverture(s)`}>
        <polygon points={contour} fill="var(--oh-accent-bg)"
                 stroke="var(--oh-text)" strokeWidth="4.5"
                 strokeLinejoin="round" />

        {/* 🔴 LES NUMÉROS DE MURS — sans eux, « sur quel mur ? » est une
            question SANS RÉFÉRENT, et le visiteur répond au hasard. ⚠️ Une
            réponse au hasard produit une donnée FAUSSE QUI A L'AIR JUSTE :
            la porte se pose quelque part, avec l'aplomb d'une cote mesurée.
            ⚠️ Posés à l'INTÉRIEUR : dehors, ils percuteraient les cotations.
            🔴 ET DESSINÉS AVANT LES OUVERTURES : le SVG peint dans l'ordre du
            document. Placés après, les pastilles RECOUVRAIENT le « F » de la
            fenêtre — invisible, alors que mon contrôle de collision ne
            comparait que TEXTE contre TEXTE et ⛔ ne pouvait pas le voir. */}
        {(geometrie.numeros_murs || []).map((n) => (
          <g key={`m${n.mur}`}>
            <circle cx={X(n.x_cm)} cy={Y(n.y_cm)} r="13"
                    fill="var(--oh-bg-elevated)" stroke="var(--oh-accent)"
                    strokeWidth="1.5" />
            <text x={X(n.x_cm)} y={Y(n.y_cm) + 5} textAnchor="middle"
                  fontSize="14" fontWeight="600" fill="var(--oh-accent-text)"
                  data-testid={`releve-numero-mur-${n.mur}`}>{n.numero}</text>
          </g>
        ))}

        {/* 🔴 LES OUVERTURES, SUR LEUR MUR. Le serveur a déjà ramené chacune
            sur son segment (`segments_ouvertures`) : ici on TRACE, ⛔ on ne
            recalcule pas. Avant, l'écran lisait `o.x_cm` — un champ qui
            n'existe pas — et toutes s'effondraient sur le coin. */}
        {ouvertures.map((o, i) => (
          <g key={i}>
            <line x1={X(o.x1_cm)} y1={Y(o.y1_cm)}
                  x2={X(o.x2_cm)} y2={Y(o.y2_cm)}
                  stroke="var(--oh-bg-elevated)" strokeWidth="7"
                  strokeLinecap="butt" />
            <line x1={X(o.x1_cm)} y1={Y(o.y1_cm)}
                  x2={X(o.x2_cm)} y2={Y(o.y2_cm)}
                  stroke={TEINTE[o.nature] || "var(--oh-accent-solid)"}
                  strokeWidth="3.5" strokeLinecap="butt" />
            {/* 🔴 L'ÉTIQUETTE SE DÉCALE VERS L'INTÉRIEUR de la pièce.
                Posée « 4 px au-dessus », elle tombait SUR le mur — et sur un
                mur vertical, exactement là où passe déjà la cote extérieure.
                Le « P » de la porte était donc invisible. ⇒ On la pousse vers
                le centre, là où il n'y a que du vide. */}
            {/* 🔴 L'ÉTIQUETTE SE POSE AU QUART DE L'OUVERTURE, ⛔ PAS À SON
                MILIEU. Le milieu d'une ouverture centrée et le milieu de son
                mur tombent au MÊME endroit — et le numéro de mur y est déjà.
                Mesuré : « 3 » et « F » se chevauchaient, lisibles seulement à
                la loupe. En se posant au quart, l'étiquette quitte la zone du
                numéro sans quitter son ouverture. */}
            <text x={X(o.x1_cm) + (X(o.x2_cm) - X(o.x1_cm)) * 0.25
                     + Math.sign(X(cx) - (X(o.x1_cm) + X(o.x2_cm)) / 2) * 12}
                  y={Y(o.y1_cm) + (Y(o.y2_cm) - Y(o.y1_cm)) * 0.25
                     + Math.sign(Y(cy) - (Y(o.y1_cm) + Y(o.y2_cm)) / 2) * 12 + 5}
                  textAnchor="middle" fontSize="15" fontWeight="600"
                  fill={TEINTE[o.nature] || "var(--oh-accent-solid)"}>
              {SIGLE[o.nature] || "?"}
            </text>
          </g>
        ))}

        {/* 🔑 LES COTATIONS, sur la NORMALE SORTANTE calculée par le serveur.
            ⚠️ Sur une pièce en L, la direction centre→mur pointe vers
            l'INTÉRIEUR : c'est le défaut déjà corrigé sur le PDF, et l'écran
            hérite du correctif au lieu de le refaire. */}
        {cotations.map((c) => {
          // 🔴 LA DIRECTION VIENT DU SERVEUR, LA DISTANCE EST DU RENDU.
          // `cotations_murs` décale de 26 CENTIMÈTRES — une distance
          // GÉOMÉTRIQUE, juste sur une A4 où l'échelle est calibrée. À
          // l'écran, une pièce de 340 cm rendue en 340 px met ces 26 cm à
          // 18 px : moins large que « 200 cm », qui chevauchait donc le mur.
          // ⇒ On garde la NORMALE calculée par le serveur (elle sort du
          // polygone, y compris sur une pièce concave — ⛔ on ne la recalcule
          // pas) et on la reprojette à une distance CONSTANTE en pixels.
          const m = (geometrie.murs || [])[c.mur];
          const a = m && sommets[m.a], b = m && sommets[m.b];
          let px = X(c.x_cm), py = Y(c.y_cm);
          if (a && b) {
            const mx = X((a.x_cm + b.x_cm) / 2), my = Y((a.y_cm + b.y_cm) / 2);
            const dx = px - mx, dy = py - my;
            const n = Math.hypot(dx, dy) || 1;
            px = mx + (dx / n) * 30;
            py = my + (dy / n) * 30 + 5;   // ⚠️ +5 : ligne de base du texte
          }
          return (
          <text key={c.mur} x={px} y={py} textAnchor="middle"
                fontSize="14" fill="var(--oh-text)"
                data-testid={`releve-cote-${c.mur}`}>
            {c.longueur_cm} cm
          </text>);
        })}

        {surface ? (
          <text x={X(cx)} y={Y(cy)} textAnchor="middle" fontSize="19"
                fontWeight="600" fill="var(--oh-text)">{surface} m²</text>
        ) : null}
      </svg>

      {/* 🔴 LA CLÉ DE LECTURE — la MÊME que celle du PDF remis à l'ANAH.
          « P » et « F » ne veulent rien dire pour qui ne connaît pas la
          convention : sans clé, on devine. ⛔ Et un visiteur qui devine son
          propre plan ne peut pas le corriger. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px",
                    justifyContent: "center", margin: "8px 0 2px",
                    fontSize: 12, color: "var(--oh-text-secondary)" }}
           data-testid="releve-plan-legende">
        <span><strong style={{ color: "var(--oh-accent-solid)" }}>P</strong> porte</span>
        <span><strong style={{ color: "var(--oh-stat-cyan-text)" }}>F</strong> fenêtre</span>
        <span><strong style={{ color: "var(--oh-text)" }}>120 cm</strong> cote, en centimètres</span>
      </div>

      {/* ⚠️ On DIT ce que le plan porte : sans ce compte, un plan sans
          ouverture ressemble à un plan complet. */}
      <p style={{ margin: "6px 0 0", fontSize: 12,
                  color: "var(--oh-text-muted)", textAlign: "center" }}
         data-testid="releve-plan-resume">
        {/* ⚠️ On distingue ce qui est DESSINÉ de ce qui est SAISI : une
            ouverture sans emplacement n'apparaît pas, et un plan qui n'en dit
            rien passerait pour complet. */}
        {(geometrie.numeros_murs || []).length} murs numérotés ·{" "}
        {ouvertures.length} ouverture(s) placée(s) · {cotations.length} cote(s)
      </p>
    </div>
  );
}

export default function AssistantReleve({ dossierId, zoneId, zoneLabel,
                                          tenantId, jeton, onFerme }) {
  const [etat, setEtat] = useState(null);
  // 🔴 UN ENSEMBLE DE QUESTIONS SAUTÉES, ⛔ PAS UN INDEX. La liste des
  // restantes RÉTRÉCIT à chaque réponse (le serveur marque la question
  // `a_demander: false`). Avec un index qu'on incrémentait en plus, répondre à
  // la première faisait passer à la TROISIÈME : une question sautée en
  // silence, à chaque réponse. ⇒ On désigne la question par ce qui reste
  // RÉELLEMENT à demander, moins ce que l'opérateur a explicitement passé.
  const [sautees, setSautees] = useState([]);
  const [valeur, setValeur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [photos, setPhotos] = useState([]);
  const fichier = useRef(null);

  const entetes = useCallback(() => ({
    "X-Tenant-ID": tenantId || "tef",
    ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
  }), [tenantId, jeton]);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await fetch(
        `${API}/api/dal/releve/${dossierId}/${zoneId}`,
        { headers: entetes(), credentials: "include" });
      if (!r.ok) throw new Error(`Relevé indisponible (${r.status})`);
      setEtat(await r.json());
    } catch (e) { setErreur(e.message); }
  }, [dossierId, zoneId, entetes]);

  useEffect(() => { charger(); }, [charger]);

  // ⚠️ Les questions RESTANTES seulement : celles déjà satisfaites sont rendues
  // par le serveur avec `a_demander: false`. ⛔ Les reposer serait la ressaisie
  // que tout ce lot existe pour supprimer.
  const restantes = (etat?.questions || []).filter((q) => q.a_demander !== false);
  const question = restantes.find((q) => !sautees.includes(q.cle)) || null;

  const repondre = async (brute) => {
    if (!question) return;
    setOccupe(true); setErreur(null);
    try {
      const r = await fetch(
        `${API}/api/dal/releve/${dossierId}/${zoneId}/reponse`,
        { method: "POST", credentials: "include",
          headers: { ...entetes(), "Content-Type": "application/json" },
          // 🔑 `origine: "mobile"` est CONSIGNÉE. Quand une cote terrain et une
          // correction bureau divergent, savoir laquelle vient d'où est la
          // seule façon de trancher.
          body: JSON.stringify({ cle: question.cle, valeur: brute,
                                 origine: "mobile" }) });
      const corps = await r.json();
      if (!r.ok) { setErreur(corps.detail || `Refus (${r.status})`); return; }
      // 🔑 L'ÉTAT VIENT DU SERVEUR — plan compris. ⛔ Pas de projection locale
      // qui divergerait du DAL final.
      // ⚠️ ⛔ AUCUN avancement manuel : la question répondue DISPARAÎT des
      // restantes, donc la suivante devient la première. Avancer en plus
      // sauterait une question.
      setEtat(corps);
      setValeur("");
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  // ── 🔴 DEC-050 — LA PHOTO PART AVEC SA ZONE, dès la prise ────────────
  const prendrePhoto = async (f) => {
    if (!f) return;
    setOccupe(true); setErreur(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // ⚠️ `zoneId` vient de la zone EN COURS, ⛔ jamais d'un choix fait après
      // coup : c'est ce qui rend le rattachement vrai par construction.
      fd.append("zone", zoneId);
      const r = await fetch(`${API}/api/dal/upload-photo/${dossierId}`,
                            { method: "POST", headers: entetes(),
                              credentials: "include", body: fd });
      const corps = await r.json().catch(() => ({}));
      if (!r.ok) { setErreur(corps.detail || `Photo refusée (${r.status})`); return; }
      setPhotos((p) => [...p, { url: corps.url || corps.file_url, zone: zoneId }]);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  if (!etat) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Loader2 style={{ width: 22, height: 22 }} className="animate-spin" />
        {erreur && (
          <p style={{ marginTop: 10, fontSize: 13,
                      color: "var(--oh-stat-red-text)" }}>{erreur}</p>
        )}
      </div>
    );
  }

  const prog = etat.progression || {};
  const fini = !question;

  return (
    <div style={{ display: "grid", gap: 14, paddingBottom: 24 }}
         data-testid="assistant-releve">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" onClick={onFerme} style={discret}
                aria-label="Revenir aux pièces" data-testid="releve-retour">
          <ChevronLeft style={{ width: 16, height: 16 }} />
        </button>
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 500,
                      color: "var(--oh-text)" }}>{zoneLabel || zoneId}</p>
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--oh-text-secondary)" }}
             data-testid="releve-progression">
            {/* ⚠️ Le compteur DIT ce qu'il compte. `progression` n'expose pas
                de « répondues » : on le DÉRIVE, ⛔ on n'invente pas une clé. */}
            {(prog.total ?? 0) - (prog.restantes ?? 0)} / {prog.total ?? 0} répondues
            {" · "}{prog.obligatoires_restantes ?? 0} obligatoire(s) restante(s)
          </p>
        </div>
      </div>

      <PlanEnConstruction geometrie={etat.plan} surface={etat.surface_m2} />

      {erreur && (
        <div style={{ ...cadre, background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", fontSize: 13 }}
             data-testid="releve-erreur">{erreur}</div>
      )}

      {fini ? (
        <div style={{ ...cadre, background: "var(--oh-stat-green-bg)",
                      color: "var(--oh-stat-green-text)" }}
             data-testid="releve-termine">
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            <Check style={{ width: 16, height: 16, verticalAlign: "-3px" }} />
            {" "}Relevé de cette pièce terminé
            {etat.surface_m2 ? ` — ${etat.surface_m2} m²` : ""}.
            {/* ⚠️ On dit ce qui suit, ⛔ on ne laisse pas l'opérateur deviner. */}
            {" "}Vous pouvez photographier la pièce, puis passer à la suivante.
          </p>
        </div>
      ) : (
        <div style={cadre} data-testid={`releve-question-${question.cle}`}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500,
                      color: "var(--oh-accent-text)" }}>
            {ETAPES[question.etape] || question.etape}
          </p>
          <p style={{ margin: "4px 0 2px", fontSize: 17, lineHeight: 1.4,
                      color: "var(--oh-text)" }}>{question.libelle}</p>
          {question.aide && (
            <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.6,
                        color: "var(--oh-text-secondary)" }}>{question.aide}</p>
          )}

          {question.type === "choix" ? (
            <div style={{ display: "grid", gap: 8 }}>
              {(question.options || []).map((o) => (
                <button key={o.valeur} type="button" disabled={occupe}
                        data-testid={`releve-choix-${o.valeur}`}
                        onClick={() => repondre(o.valeur)}
                        style={{ ...discret, minHeight: 50, fontSize: 15,
                                 textAlign: "left", color: "var(--oh-text)" }}>
                  {o.libelle}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={saisie} value={valeur}
                       data-testid="releve-saisie"
                       // ⚠️ `inputMode="decimal"` ouvre le PAVÉ NUMÉRIQUE sur
                       // mobile. ⛔ Un clavier texte pour saisir « 320 » fait
                       // perdre trois gestes par cote, onze fois par pièce.
                       inputMode={question.type === "nombre" ? "decimal" : "text"}
                       autoFocus
                       placeholder={question.type === "nombre" ? "0" : "…"}
                       onChange={(e) => setValeur(e.target.value)}
                       onKeyDown={(e) => {
                         if (e.key === "Enter" && valeur.trim()) repondre(valeur);
                       }} />
                {question.unite && (
                  <span style={{ fontSize: 16, color: "var(--oh-text-secondary)" }}>
                    {question.unite}
                  </span>
                )}
              </div>
              <button type="button" style={{ ...primaire, marginTop: 10 }}
                      disabled={occupe || !valeur.trim()}
                      data-testid="releve-valider"
                      onClick={() => repondre(valeur)}>
                {occupe && <Loader2 style={{ width: 16, height: 16 }}
                                    className="animate-spin" />}
                Valider
              </button>
            </>
          )}

          {/* 🔑 « JE NE SAIS PAS » EST UNE RÉPONSE, et elle est OFFERTE.
              ⚠️ Sans elle, l'opérateur invente une valeur pour avancer — et une
              cote inventée part à l'ANAH. Une question sautée se rattrape au
              bureau ; une valeur fausse, non. */}
          {!question.obligatoire && (
            <button type="button" style={{ ...discret, marginTop: 8, width: "100%" }}
                    disabled={occupe} data-testid="releve-passer"
                    onClick={() => {
                      // ⚠️ Passer ⛔ n'écrit RIEN. La question reste
                      // « à demander » côté serveur — elle se rattrape au
                      // bureau. ⛔ Écrire une valeur vide la ferait passer
                      // pour renseignée.
                      setValeur("");
                      setSautees((s) => [...s, question.cle]);
                    }}>
              Je ne sais pas — passer
            </button>
          )}
        </div>
      )}

      {/* ═══ 🔴 DEC-050 — LES PHOTOS, DEPUIS LA PIÈCE EN COURS ═══════════ */}
      <div style={cadre} data-testid="releve-photos">
        <p style={{ margin: "0 0 8px", fontSize: 13,
                    color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
          {/* ⚠️ On NOMME la pièce à laquelle la photo sera rattachée. Un
              « Ajouter une photo » muet laisserait croire qu'on la classera
              plus tard — et c'est exactement ce qui produisait des photos
              rattachées à la mauvaise pièce. */}
          Les photos prises ici seront rattachées à{" "}
          <strong style={{ color: "var(--oh-text)" }}>{zoneLabel || zoneId}</strong>.
        </p>
        <input ref={fichier} type="file" accept="image/*" capture="environment"
               style={{ display: "none" }} data-testid="releve-photo-input"
               onChange={(e) => { prendrePhoto(e.target.files?.[0]);
                                  e.target.value = ""; }} />
        <button type="button" style={primaire} disabled={occupe}
                data-testid="releve-photo"
                onClick={() => fichier.current && fichier.current.click()}>
          <Camera style={{ width: 18, height: 18 }} />
          Photographier {zoneLabel || zoneId}
        </button>
        {photos.length > 0 && (
          <p style={{ margin: "8px 0 0", fontSize: 12.5,
                      color: "var(--oh-text-secondary)" }}
             data-testid="releve-photos-compte">
            {photos.length} photo(s) rattachée(s) à cette pièce.
          </p>
        )}
      </div>

      {(etat.geometrie?.incoherences || []).length > 0 && (
        <div style={{ ...cadre, background: "var(--oh-stat-amber-bg)",
                      color: "var(--oh-stat-amber-text)" }}
             data-testid="releve-incoherences">
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
            <AlertTriangle style={{ width: 15, height: 15, verticalAlign: "-2px" }} />
            {" "}
            {/* ⚠️ On SIGNALE, ⛔ on ne corrige pas : c'est l'opérateur qui est
                devant la pièce, pas nous. */}
            {etat.geometrie.incoherences.join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}
